import {
    useForm,
    UseFormProps,
    SubmitHandler,
    FieldValues,
    FormProvider as ReactHookFormProvider,
} from 'react-hook-form';
import * as Yup from 'yup';
import { useEffect } from 'react';
import { useYupValidationResolver } from './validationResolver';

export interface FormProviderProps<
    TDefaultValues extends UseFormProps['defaultValues']
> {
    defaultValues: TDefaultValues;
    validationSchema: Yup.AnyObjectSchema;
    children: React.ReactNode;
    onSubmit?: SubmitHandler<FieldValues>;
}

/**
 * Custom form logic provider wrapper around react-hook-forms api
 * @param {object} defaultValues - default form values object
 * @param {SubmitHandler<FieldValues>} onSubmit - submit handler for custom operations
 * @param {Yup.AnyObjectSchema} validationSchema - Yup validation schema to define various rules for associated form values
 * @param {React.ReactNode} children - React Nodes
 * @returns Custom form provider
 */
export function FormProvider<
    TDefaultValues extends UseFormProps['defaultValues']
>({
    defaultValues,
    validationSchema,
    children,
}: FormProviderProps<TDefaultValues>) {
    const resolver = useYupValidationResolver(validationSchema);

    const methods = useForm({
        defaultValues: defaultValues,
        resolver,
        shouldFocusError: true,
    });

    useEffect(() => {
        methods.reset(defaultValues);
    }, [defaultValues, methods]);

    /* "handleSubmit" will validate your inputs before invoking "onSubmit" */
    return (
        <ReactHookFormProvider {...methods}>
            <form>{children}</form>
        </ReactHookFormProvider>
    );
}

interface MockFormProviderProps {
    children: React.ReactNode;
    removeValidation?: boolean;
}

/**
 * mock FormProvider wrapper for testing purposes
 * @param children - Form inputs to test
 * @param includeValidation - whether to include validation for testing
 * @returns MockFormProvider
 */
export function MockFormProvider({
    children,
    removeValidation,
}: MockFormProviderProps) {
    const mockDefaultValues = {
        stringField: '',
        selectField: '',
        numberField: '',
        booleanField: false,
        multiSelectField: [],
        dateField: new Date(),
    };

    const mockValidationSchema = Yup.object({
        stringField: Yup.string().min(1).max(10).required(),
        numberField: Yup.number().min(2).max(8).required(),
        booleanField: Yup.boolean().required(),
        multiSelectField: Yup.array().min(1).required(),
        selectField: Yup.string().required(),
        dateField: Yup.date().required(),
    }).required();

    const onSubmitHandler = (e: any) => e.preventDefault();

    return (
        <FormProvider
            defaultValues={mockDefaultValues}
            validationSchema={
                removeValidation ? Yup.object({}) : mockValidationSchema
            }
            onSubmit={onSubmitHandler}
        >
            {children}
        </FormProvider>
    );
}
